import {
    contextualHelpUrlSelector,
    showHelpBarSelector,
    toggleShowHelpBar
} from 'features/contextual_help/contextualHelpSlice'
import { useClickOutSide } from 'hooks'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import { colors, _ } from 'utils'
import Icon from '../../components/Icon'

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`

const S = {
    Wrapper: styled.div`
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        overflow: hidden;
        z-index: 999;
        background: rgba(0, 0, 0, 0.58);
        transition: 300ms;
    `,
    Sidebar: styled.div<{ show: boolean, expand: boolean }>`
        position: absolute;
        top: 0;
        right: 0;
        width: ${({ expand }) => (expand ? '66.6vw' : '420px')};
        height: 100vh;
        background-color: white;
        box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.25);
        transform: translateX(${({ show }) => (show ? 0 : '100%')});
        transition: 300ms;
    `,
    Loader: styled.div`
        position: absolute;
        top: 45%;
        left: 42%;
        transform: translate(50%, -50%);
        border: 6px solid #f3f3f3;
        border-radius: 50%;
        border-top: 6px solid ${colors.darkOpacity(0.75)};
        width: 80px;
        height: 80px;
        animation: ${spin} 2s linear infinite;
        z-index: -1;
    `,
    Header: styled.div`
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        font-size: 20px;
        font-weight: 700;
        color: ${colors.grayTitleOpacity(0.9)};
        box-shadow: inset 0 -3px 3px rgba(0, 0, 0, 0.05);
    `,
    CloseIcon: styled(Icon)`
        margin-bottom: 3px;
    `,
    IframeWrapper: styled.div`
        width: 100%;
        height: calc(100% - 49px);
    `,
    ExpandButton: styled.div<{ expand: boolean }>`
        position: absolute;
        top: 50%;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 62px;
        transform: translate(-100%, -50%);
        background-color: #F1F4F5;
        border-radius: 4px 0 0 4px;
        cursor: pointer;
        & > span {
            transform: ${({ expand }) => (expand ? 'rotate(-90deg)' : 'rotate(90deg)')};
            margin-right: ${({ expand }) => (expand ? '2px' : '-6px')};
        }
    `
}

const ContextualHelpDrawer = () => {
    const dispatch = useDispatch()
    const [immediateShow, setImmediateShow] = useState(false)
    const showHelpBar = useSelector(showHelpBarSelector)
    const contextualHelpUrl = useSelector(contextualHelpUrlSelector)
    const [expand, setExpand] = useState(false)
    const contentRef = useRef<HTMLDivElement>()
    const handleClose = () => {
        if (!immediateShow) {
            return
        }
        setImmediateShow(false)
        // Wait 0.3s for slider animation
        setTimeout(() => dispatch(toggleShowHelpBar()), 300)
    }

    useClickOutSide(contentRef, handleClose)

    useEffect(() => {
        setImmediateShow(showHelpBar)
    }, [showHelpBar])

    return (
        showHelpBar && (
            <S.Wrapper>
                <S.Sidebar show={immediateShow} ref={contentRef} expand={expand}>
                    <S.ExpandButton onClick={() => setExpand(!expand)} expand={expand}>
                        <Icon icon="chevronDown" size={24} />
                    </S.ExpandButton>
                    <S.Loader />
                    <S.Header>
                        <span>{_('contextual_help.header')}</span>
                        <S.CloseIcon icon="close" size={20} onClick={handleClose} />
                    </S.Header>
                    <S.IframeWrapper>
                        <iframe
                            width="100%"
                            height="100%"
                            title="help-content"
                            src={contextualHelpUrl}
                            frameBorder={0}
                            allowFullScreen
                            allow="autoplay; fullscreen; camera; microphone"
                        />
                    </S.IframeWrapper>
                </S.Sidebar>
            </S.Wrapper>
        )
    )
}

export default ContextualHelpDrawer
